'use client'

/**
 * The ONE presentational shell for the thin site-wide notices that live under the navbar
 * (premium state, email verification). Both used to hand-roll their own bar and had drifted into
 * two slightly different looks stacked on top of each other.
 *
 * Owner, 2026-08-10, on the post-signup screen: "banners are ugly and overlap chat can we make
 * them better". Two things were wrong and both are fixed here:
 *
 *  1. FULL-BLEED TINTED BARS. `w-full border-b bg-*-500/10` on a dark page reads as a broken strip,
 *     and the title wrapped to two lines while the message sat on one, so the row came out ragged.
 *     This is an inset card: rounded, bordered, one line, message truncates instead of wrapping.
 *  2. THE OVERLAP. The bar spanned the whole viewport while its content was `max-w-[1200px]
 *     mx-auto`, centred on the VIEWPORT rather than on the content band between the left sidebar
 *     and the chat dock. At 1920px it just cleared the dock; at ~1500px the CTA sat underneath it.
 *     <SubBannerSlot/> now owns that geometry and this component just fills the space it is given.
 */

import Link from 'next/link'
import { X, type LucideIcon } from 'lucide-react'

export type BannerTone = 'amber' | 'red' | 'blue'

// Redesign 2026-08-23 (owner: "it's ugly, make it best in class"). The bars were full saturated
// dark-tinted shells (bg-amber-950/80 etc.), which read muddy on a dark page. Now every tone shares
// ONE neutral glass shell and expresses itself only through a thin left accent, a soft icon chip and
// the CTA — calm chrome instead of a colored strip, same one-line footprint. The `accent` is a
// gradient for the 2px edge; `icon` tints the chip; `cta` differs by intent: amber/blue are sells so
// they keep a solid accent button, red is a service alert (payment failed) so its CTA goes quiet.
const TONE: Record<BannerTone, { accent: string; icon: string; cta: string }> = {
  amber: {
    accent: 'from-amber-400 to-amber-400/20',
    icon: 'bg-amber-400/10 text-amber-300 ring-amber-400/20',
    cta: 'bg-gradient-to-b from-amber-300 to-amber-500 text-black hover:from-amber-200 hover:to-amber-400',
  },
  red: {
    accent: 'from-red-400 to-red-400/20',
    icon: 'bg-red-400/10 text-red-300 ring-red-400/20',
    cta: 'bg-red-400/12 text-zinc-50 ring-1 ring-red-400/35 hover:bg-red-400/20',
  },
  blue: {
    accent: 'from-sky-400 to-sky-400/20',
    icon: 'bg-sky-400/10 text-sky-300 ring-sky-400/20',
    cta: 'bg-gradient-to-b from-sky-300 to-sky-500 text-black hover:from-sky-200 hover:to-sky-400',
  },
}

export default function SiteBanner({
  tone, Icon, title, message, cta, onDismiss,
}: {
  tone: BannerTone
  Icon: LucideIcon
  title: string
  /** Secondary line. Hidden under lg so the row never wraps on a narrow content band. */
  message?: string
  cta?: { label: string; href?: string; onClick?: () => void; disabled?: boolean }
  onDismiss: () => void
}) {
  const t = TONE[tone]
  const ctaClass = `shrink-0 rounded-lg px-3.5 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-60 ${t.cta}`

  return (
    <div
      role="status"
      className="pointer-events-auto relative mx-2 mt-2 flex items-center gap-3 overflow-hidden rounded-xl border border-zinc-800/90 bg-zinc-950/90 px-3 py-2 text-zinc-100 shadow-lg backdrop-blur-md sm:mx-3"
    >
      {/* Thin tone accent on the left edge — the only saturated color, so the bar stays calm glass. */}
      <span aria-hidden className={`pointer-events-none absolute inset-y-2 left-0 w-[2px] rounded-full bg-gradient-to-b ${t.accent}`} />
      <span className={`ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ring-1 ${t.icon}`}>
        <Icon className="h-4 w-4" />
      </span>
      {/* min-w-0 is what lets the truncate below actually engage inside a flex row. */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium leading-tight tracking-[-0.01em] text-zinc-50">{title}</p>
        {message && <p className="hidden truncate text-[12px] leading-tight text-zinc-400 lg:block">{message}</p>}
      </div>
      {cta && (cta.href
        ? <Link href={cta.href} className={ctaClass}>{cta.label}</Link>
        : <button type="button" onClick={cta.onClick} disabled={cta.disabled} className={ctaClass}>{cta.label}</button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="-mr-0.5 shrink-0 rounded-md p-1.5 text-zinc-500 transition-colors hover:text-zinc-300"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
